const DATASOURCE_CONSTANTS = Object.freeze({
  FIELD_TYPES: Object.freeze({
    DROPDOWN: 'DROPDOWN',
    DROPDOWN_LOOKUP: 'DROPDOWN_LOOKUP',
    DROPDOWN_LOOKUP_SECTION: 'DROPDOWN_LOOKUP_SECTION',
  }),
  SOURCE_TYPES: Object.freeze({
    AUTO_COMPLETE: 'AUTO_COMPLETE',
    DATASOURCE_ID: 'datasource_id',
    SUB_ITEM_ID: 'sub_item_id',
  }),
});

export const getNativeDropdownSourceFieldType = (fieldName, nativeDropdownSourceFieldNames) =>
  new Set(nativeDropdownSourceFieldNames).has(fieldName) ? DATASOURCE_CONSTANTS.FIELD_TYPES.DROPDOWN : DATASOURCE_CONSTANTS.SOURCE_TYPES.AUTO_COMPLETE;

export const isChoiceSourceReferenceField = formValues => {
  const reference = formValues?.field_options?.reference;
  return reference === 'true' || reference === true;
};

export const resolveChoiceSourceFieldMetadata = (choiceDataSources, { dataSourceId, referenceFieldName } = {}) => {
  const normalizedSourceId = String(dataSourceId ?? '');
  const normalizedFieldName = String(referenceFieldName ?? '');

  if (!Array.isArray(choiceDataSources) || normalizedSourceId === '' || normalizedFieldName === '') {
    return null;
  }

  const source = choiceDataSources.find(item => String(item.value) === normalizedSourceId);
  if (!source || !Array.isArray(source.fields)) {
    return null;
  }

  return source.fields.find(field => field.value === normalizedFieldName || field.column_name === normalizedFieldName) ?? null;
};

const DROPDOWN_NODE_FIELD_TYPES = [DATASOURCE_CONSTANTS.FIELD_TYPES.DROPDOWN_LOOKUP, DATASOURCE_CONSTANTS.FIELD_TYPES.DROPDOWN_LOOKUP_SECTION];

// The form builder canvas only recognizes the 'DROPDOWN' node type, so lookup-backed
// dropdown fields need to be normalized to it before being handed to fw-form-builder.
export const mapDropdownLookupFieldsToDropdownType = fields => {
  if (!Array.isArray(fields)) {
    return fields;
  }

  return fields.map(field => (DROPDOWN_NODE_FIELD_TYPES.includes(field?.type) ? { ...field, type: DATASOURCE_CONSTANTS.FIELD_TYPES.DROPDOWN } : field));
};

export const DEPENDENT_LOOKUP_LEVEL_COUNT = 3;

// Only the first level can be mandatory, and only when the parent field itself is marked required - the
// remaining levels cascade off it and are always optional.
export const buildDependentLookupFields = (parentName, parentLabel, isParentRequired = false) => {
  return Array.from({ length: DEPENDENT_LOOKUP_LEVEL_COUNT }, (_, index) => {
    const level = index + 1;
    return {
      name: `${parentName}_dd${level}`,
      label: `${parentLabel} - ${level}`,
      type: DATASOURCE_CONSTANTS.FIELD_TYPES.DROPDOWN_LOOKUP,
      required: !!isParentRequired && level === 1,
      editable: true,
      filterable: true,
      searchable: true,
      is_dependent: false,
      choices: [],
    };
  });
};

export const buildDropdownLookupFieldSavePayload = (formValues, choiceDataSources, nativeDropdownSourceFieldNames) => {
  if (!isChoiceSourceReferenceField(formValues)) {
    return null;
  }

  const fieldOptions = formValues?.field_options ?? {};
  const dataSourceId = fieldOptions.data_source;

  if (dataSourceId == null || dataSourceId === '') {
    return null;
  }

  const referenceFieldName = formValues.referenceField || formValues.column_name || fieldOptions.lookup_field_name || '';
  if (!referenceFieldName) {
    return null;
  }

  const fieldMetadata = resolveChoiceSourceFieldMetadata(choiceDataSources, {
    dataSourceId,
    referenceFieldName,
  });

  const lookupFieldId = fieldOptions.lookup_field_id || fieldMetadata?.id;
  if (!lookupFieldId) {
    return null;
  }

  const levels = [{ source_type: DATASOURCE_CONSTANTS.SOURCE_TYPES.DATASOURCE_ID, lookup_id: String(dataSourceId) }];
  const subItemId = fieldOptions[DATASOURCE_CONSTANTS.SOURCE_TYPES.SUB_ITEM_ID];

  if (subItemId != null && subItemId !== '') {
    levels.push({ source_type: DATASOURCE_CONSTANTS.SOURCE_TYPES.SUB_ITEM_ID, lookup_id: String(subItemId) });
  }

  const hasDependents = !!(formValues.has_dependents || fieldMetadata?.has_dependents);

  return {
    type: hasDependents ? DATASOURCE_CONSTANTS.FIELD_TYPES.DROPDOWN_LOOKUP_SECTION : DATASOURCE_CONSTANTS.FIELD_TYPES.DROPDOWN_LOOKUP,
    field_options: {
      ...(fieldMetadata?.field_options ?? {}),
      // The backend expects this sub-config as an opaque JSON string, not a nested object - getLookupConfig()
      // parses it back on the read side.
      lego_lookup_config: JSON.stringify({
        levels,
        lookup_field_id: String(lookupFieldId),
        source_field_type: getNativeDropdownSourceFieldType(referenceFieldName, nativeDropdownSourceFieldNames),
      }),
    },
  };
};

export const mapNativeDropdownSources = apiResponse => {
  const nativeDropdown = apiResponse?.native_dropdown;
  if (!Array.isArray(nativeDropdown)) {
    return [];
  }

  return nativeDropdown.map(item => ({
    value: String(item.id),
    text: item.label,
    has_sub_items: !!item.has_sub_items,
    fields: [],
    subItems: [],
  }));
};

export const mapNativeDropdownSubItems = apiResponse => {
  const items = apiResponse?.items;
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map(item => ({
    value: String(item.id),
    text: item.label,
  }));
};

export const mapNativeDropdownFields = apiResponse => {
  const fields = apiResponse?.fields;
  if (!Array.isArray(fields)) {
    return [];
  }

  return fields.map(field => {
    const id = field.id != null ? String(field.id) : null;
    const hasDependents = !!(field.is_dependent_field || field.is_category_dep_dd);

    const mappedField = {
      value: field.name,
      text: field.label,
      column_name: field.name,
      option_value_path: 'id',
      option_label_path: 'value',
      has_dependents: hasDependents,
    };

    if (id != null) {
      mappedField.id = id;
    }

    if (field.field_options) {
      mappedField.field_options = field.field_options;
    }

    return mappedField;
  });
};

export const mergeFieldsIntoChoiceSources = (sources, sourceId, fields) => {
  const normalizedSourceId = String(sourceId ?? '');
  if (!Array.isArray(sources) || normalizedSourceId === '') {
    return sources ?? [];
  }

  return sources.map(source => {
    if (String(source.value) !== normalizedSourceId) {
      return source;
    }
    return {
      ...source,
      fields: [...fields],
    };
  });
};

const getLookupConfig = field => {
  const lookupConfig = field?.field_options?.lego_lookup_config;
  if (typeof lookupConfig !== 'string') {
    return lookupConfig;
  }

  try {
    return JSON.parse(lookupConfig);
  } catch (error) {
    console.error('Error parsing lego_lookup_config - ' + error);
    return undefined;
  }
};

export const buildNativeDropdownChoicesFilterParams = fields => {
  const lookupFields = (fields ?? []).filter(field => getLookupConfig(field)?.lookup_field_id != null);

  return lookupFields.reduce((params, field, index) => {
    const config = getLookupConfig(field);
    const datasourceId = config.levels?.find(level => level.source_type === DATASOURCE_CONSTANTS.SOURCE_TYPES.DATASOURCE_ID)?.lookup_id;
    const subItemId = config.levels?.find(level => level.source_type === DATASOURCE_CONSTANTS.SOURCE_TYPES.SUB_ITEM_ID)?.lookup_id;

    if (datasourceId == null) {
      return params;
    }

    params[`f[${index}][${DATASOURCE_CONSTANTS.SOURCE_TYPES.DATASOURCE_ID}]`] = datasourceId;
    params[`f[${index}][field_id]`] = config.lookup_field_id;
    if (subItemId != null) {
      params[`f[${index}][${DATASOURCE_CONSTANTS.SOURCE_TYPES.SUB_ITEM_ID}]`] = subItemId;
    }
    return params;
  }, {});
};

export const mapNativeDropdownChoicesResponse = apiResponse => {
  const choices = apiResponse?.choices;
  const choicesByFieldId = new Map();
  if (!Array.isArray(choices)) {
    return choicesByFieldId;
  }

  choices.forEach(entry => {
    if (entry?.field_id == null) return;
    choicesByFieldId.set(String(entry.field_id), {
      isDependent: !!entry.is_dependent,
      levels: entry.levels,
      choices: entry.choices ?? [],
    });
  });
  return choicesByFieldId;
};

// Maps API `nested_options` (children) to the `choices` key fw-nested-select/fw-co-nested-select expect,
// and stamps each option with the field `name` the next cascade level should update (from `levels` metadata).
const mapDependentChoiceTree = (options, levels, depth) => {
  if (!Array.isArray(options)) {
    return [];
  }

  const nextLevelName = levels?.[`level${depth + 2}`]?.name;

  return options.map(option => {
    const { nested_options: nestedOptions, ...rest } = option;
    const mapped = { ...rest };
    if (nestedOptions?.length) {
      mapped.choices = mapDependentChoiceTree(nestedOptions, levels, depth + 1);
    }
    if (nextLevelName) {
      mapped.name = nextLevelName;
    }
    return mapped;
  });
};

// fw-co-nested-select tracks selection by display value (see getFormSchema's DROPDOWN_LOOKUP_SECTION
// branch) and its fwChange only ever emits the leaf's own value (see its `getLeafValue`), with no
// information about which parent branch was selected - so the record payload must walk the choice tree
// to resolve the id at every level along the selected path, root to leaf (parseSchemaFields sends one key
// per level - <field.name>_dd<level> - see buildDependentLookupFields), not just the leaf's own id.
// KNOWN LIMITATION: this is an unscoped depth-first search - it returns the path to the first node anywhere
// in the tree whose value matches, regardless of which parent branch was actually selected (intentional: a
// top-level selection must resolve to its own id even though it has children, see
// FormRendererController.test.js's "resolves a top-level selection to its choice id"). If two different
// branches each contain a choice with the exact same display value, this can resolve to the wrong one -
// fixing it properly would need fw-co-nested-select to emit the full selection path instead.
const findInChoiceTree = (choices, matches, extract) => {
  for (const choice of choices ?? []) {
    if (matches(choice)) {
      return [extract(choice)];
    }

    const childPath = findInChoiceTree(choice.choices, matches, extract);
    if (childPath) {
      return [extract(choice), ...childPath];
    }
  }
};

export const findDependentChoiceIdPathByValue = (choices, value) =>
  findInChoiceTree(
    choices,
    choice => choice.value === value,
    choice => choice.id
  );

// An edit-mode field the user never touched is seeded straight from recordUpdateModalInitValues - the
// saved record's raw leaf id (see normalizeDependentLookupRecordData) - not a display value emitted via
// fw-co-nested-select's fwChange, since seeding never fires that event (see co-nested-node's
// componentWillLoad, which only emits fwPropertyChange - internal bookkeeping, not a value fw-form tracks).
// parseSchemaFields falls back to this when findDependentChoiceIdPathByValue can't match values[key]
// against any display value, so an untouched field on save still resolves to the same per-level id path a
// fresh selection would, instead of falling through to the raw-value fallback under the parent's own name.
export const findDependentChoiceIdPathById = (choices, id) => {
  if (id == null) return;
  const normalizedId = String(id);
  return findInChoiceTree(
    choices,
    choice => String(choice.id) === normalizedId,
    choice => choice.id
  );
};

const DEPENDENT_LOOKUP_KEY_PATTERN = /^(.+)_dd(\d+)$/;

// FormRendererController.parseSchemaFields saves each selected cascade level under its own dependent
// sub-field key (<field.name>_dd<level>) instead of a single value under the parent field's own name (see
// buildDependentLookupFields), so the record the API returns back is flattened the same way. Re-key it back
// to a single value under the parent field's name here - the deepest level present is the actual selection's
// own id, shallower levels are just the ids of the ancestor branches taken to reach it - so the rest of the
// record pipeline (list display, edit-form reseeding) can keep treating DROPDOWN_LOOKUP_SECTION like every
// other field that stores one value under recordData[field.name].
// When the choice tree is known (choicesByFieldId) the levels are checked deepest first (dd3, dd2, dd1) and the
// first one still present in it wins: a saved child that is no longer an option falls back to its nearest
// surviving parent, so the display and the edit form show (and reseed) the cascade down to that parent. If none
// of the saved levels are in the tree - or there are no choices at all - the value is empty.
export const normalizeDependentLookupRecordData = (fields, recordData, choicesByFieldId) => {
  if (!recordData) {
    return recordData;
  }

  const dependentFields = (fields ?? []).filter(field => field.type === DATASOURCE_CONSTANTS.FIELD_TYPES.DROPDOWN_LOOKUP_SECTION);
  if (!dependentFields.length) {
    return recordData;
  }

  const dependentFieldNames = new Set(dependentFields.map(field => field.name));
  const normalized = {};
  Object.keys(recordData).forEach(key => {
    if (!dependentFieldNames.has(key.match(DEPENDENT_LOOKUP_KEY_PATTERN)?.[1])) {
      normalized[key] = recordData[key];
    }
  });

  dependentFields.forEach(field => {
    const orderedIds = Array.from({ length: DEPENDENT_LOOKUP_LEVEL_COUNT }, (_, index) => recordData[`${field.name}_dd${index + 1}`]).filter(id => id != null);
    if (!orderedIds.length) {
      return;
    }
    // The deepest saved level still in the tree wins; none in the tree (or no choices at all) is empty.
    normalized[field.name] = findDeepestValidLevelId(getLookupChoicesForField(field, choicesByFieldId), orderedIds) ?? '';
  });

  return normalized;
};

// The saved cascade can point at ids that no longer exist in the choice tree (the datasource's choices were
// edited after the record was saved), so the leaf id alone may match nothing and the whole cascade would
// render blank. Walk the saved ids root to leaf instead and return the deepest id that is still a child of
// the previous level, so the still-valid part of the selection is kept. Returns undefined when even the first
// level is gone (or no choices are loaded), which the caller treats as empty.
const findDeepestValidLevelId = (choices, orderedIds) => {
  let nodes = choices;
  let deepestId;
  for (const id of orderedIds) {
    const node = (nodes ?? []).find(choice => String(choice.id) === String(id));
    if (!node) break;
    deepestId = node.id;
    nodes = node.choices;
  }
  return deepestId;
};

// Only the leaf id is persisted on the record, but fw-co-nested-select restores each cascading level
// independently, so it needs the display value at every depth from root to leaf, not just the leaf's own.
export const findDependentChoiceValuePathById = (choices, id) => {
  if (id == null) return;
  const normalizedId = String(id);
  return findInChoiceTree(
    choices,
    choice => String(choice.id) === normalizedId,
    choice => choice.value
  );
};

// Records store a DROPDOWN_LOOKUP field's choice id, not its display value - used to resolve the value
// shown in the records list / record detail view back from that id.
export const findChoiceValueById = (choices, id) => {
  if (id == null) {
    return;
  }
  const normalizedId = String(id);
  return (choices ?? []).find(choice => String(choice.id) === normalizedId)?.value;
};

export const getLookupChoicesForField = (field, choicesByFieldId) => {
  const lookupFieldId = getLookupConfig(field)?.lookup_field_id;
  if (lookupFieldId == null || !choicesByFieldId?.size) {
    return;
  }

  const entry = choicesByFieldId.get(String(lookupFieldId));
  if (!entry) {
    return;
  }

  return entry.isDependent ? mapDependentChoiceTree(entry.choices, entry.levels, 0) : entry.choices;
};

// FORMSERV requirement: AUTO_COMPLETE-backed lookup fields (anything other than the fixed native enum
// fields in NATIVE_DROPDOWN_SOURCE_FIELD_NAMES, see getNativeDropdownSourceFieldType) must have their
// choice id saved - and matched back against on edit - as a string. DROPDOWN-backed fields keep their
// native (typically numeric) id as-is. See parseSchemaFields/getFormSchema for where this is applied.
export const isAutoCompleteLookupSourceField = field => getLookupConfig(field)?.source_field_type === DATASOURCE_CONSTANTS.SOURCE_TYPES.AUTO_COMPLETE;

const getLookupSourceCoordinates = lookupConfig => ({
  dataSourceId: lookupConfig?.levels?.find(level => level.source_type === DATASOURCE_CONSTANTS.SOURCE_TYPES.DATASOURCE_ID)?.lookup_id,
  subItemId: lookupConfig?.levels?.find(level => level.source_type === DATASOURCE_CONSTANTS.SOURCE_TYPES.SUB_ITEM_ID)?.lookup_id,
});

// lookup_field_id of a lookup-backed dropdown, or undefined when the field has none or its config has no datasource level
// (such fields are skipped when the native-dropdown-choices params are built, so they never get a choices entry).
export const getDropdownLookupFieldId = field => {
  const lookupConfig = getLookupConfig(field);
  if (lookupConfig?.lookup_field_id == null || getLookupSourceCoordinates(lookupConfig).dataSourceId == null) {
    return undefined;
  }
  return lookupConfig.lookup_field_id;
};

const buildReferenceFieldsRequestKey = (dataSourceId, subItemId) => `${dataSourceId}:${subItemId ?? ''}`;

// One request per distinct (datasource, sub-item) pair referenced by any lookup-backed field, so the
// controller can fetch each source's field list exactly once regardless of how many fields reference it.
export const buildDropdownLookupReferenceFieldRequests = fields => {
  const requestsByKey = new Map();

  (fields ?? []).forEach(field => {
    const lookupConfig = getLookupConfig(field);
    if (lookupConfig?.lookup_field_id == null) {
      return;
    }

    const { dataSourceId, subItemId } = getLookupSourceCoordinates(lookupConfig);
    if (dataSourceId == null) {
      return;
    }

    requestsByKey.set(buildReferenceFieldsRequestKey(dataSourceId, subItemId), { dataSourceId, subItemId });
  });

  return Array.from(requestsByKey.values());
};

// Inverse of buildDropdownLookupFieldSavePayload: resolves a lookup field's data_source/referenceField
// (what the choice-source picker reads to show its current selection) from lego_lookup_config.
// True when a lookup-backed dropdown field's datasource, or the source field it picks within it, no longer exists.
// `datasourceIds` (Set of string ids) is null when the datasource list could not be loaded, and a source's field list
// is simply absent from the caches when its request failed - in both cases the answer is "unknown", i.e. not broken,
// so a transient host error can't flag a healthy field.
export const isDropdownLookupSourceMissing = (field, { datasourceIds, fieldsCache = {}, subItemFieldsCache = {} } = {}) => {
  const lookupConfig = getLookupConfig(field);
  if (lookupConfig?.lookup_field_id == null) {
    return false;
  }

  const { dataSourceId, subItemId } = getLookupSourceCoordinates(lookupConfig);
  if (dataSourceId == null) {
    return false;
  }

  const normalizedSourceId = String(dataSourceId);
  if (datasourceIds && !datasourceIds.has(normalizedSourceId)) {
    return true;
  }

  const sourceFields = subItemId == null ? fieldsCache[normalizedSourceId] : subItemFieldsCache[`${normalizedSourceId}:${subItemId}`];
  if (!Array.isArray(sourceFields)) {
    return false;
  }

  return !sourceFields.some(item => String(item.id) === String(lookupConfig.lookup_field_id));
};

export const resolveDropdownLookupReferenceMetadata = (field, referenceFieldsByRequestKey) => {
  const lookupConfig = getLookupConfig(field);
  if (lookupConfig?.lookup_field_id == null || !referenceFieldsByRequestKey?.size) {
    return null;
  }

  const { dataSourceId, subItemId } = getLookupSourceCoordinates(lookupConfig);
  if (dataSourceId == null) {
    return null;
  }

  const fields = referenceFieldsByRequestKey.get(buildReferenceFieldsRequestKey(dataSourceId, subItemId));
  const fieldMetadata = fields?.find(item => String(item.id) === String(lookupConfig.lookup_field_id));
  if (!fieldMetadata) {
    return null;
  }

  const referenceFieldName = fieldMetadata.value ?? fieldMetadata.column_name;

  return {
    field_options: {
      data_source: String(dataSourceId),
      ...(subItemId != null && { [DATASOURCE_CONSTANTS.SOURCE_TYPES.SUB_ITEM_ID]: String(subItemId) }),
    },
    referenceField: referenceFieldName,
    column_name: referenceFieldName,
  };
};

export const mergeDropdownLookupReferenceMetadataIntoFields = (fields, referenceFieldsByRequestKey) => {
  if (!Array.isArray(fields) || !referenceFieldsByRequestKey?.size) {
    return fields;
  }

  return fields.map(field => {
    const metadata = resolveDropdownLookupReferenceMetadata(field, referenceFieldsByRequestKey);
    if (!metadata) {
      return field;
    }

    return {
      ...field,
      referenceField: metadata.referenceField,
      column_name: metadata.column_name,
      field_options: { ...field.field_options, ...metadata.field_options },
    };
  });
};

export const mergeChoiceSourceData = (sources, sourceId, { fields, subItems } = {}) => {
  const normalizedSourceId = String(sourceId ?? '');
  if (!Array.isArray(sources) || normalizedSourceId === '') {
    return sources ?? [];
  }

  return sources.map(source => {
    if (String(source.value) !== normalizedSourceId) {
      return source;
    }
    return {
      ...source,
      fields: Array.isArray(fields) ? [...fields] : [],
      subItems: Array.isArray(subItems) ? [...subItems] : [],
    };
  });
};
